import React, { Component, RefObject } from 'react';
import classNames from 'classnames';
import Draggable, { DraggableEvent, DraggableData } from 'react-draggable';

import { RuleExecutor } from '../Rule';
import Formula from '../formula/Formula';
import Statement from '../Statement';
import { ScaleContext } from './AspectRatio';
import { PositionedNode, Point } from '../Position';
import { FormulaPositionMonitor } from './Subgoal';

import DragIcon from '@material-ui/icons/DragHandle';
import styles from '../styles/FormulaBox.module.css';
import Tooltip from '@material-ui/core/Tooltip';

interface Props {
  formula: Formula;

  /** Subgoal this formula is in. */
  subgoal: Statement;

  /** Callback for executing actions to change the subgoal. */
  executor: RuleExecutor;

  /** Whether dragging is allowed in this level or not. */
  draggingEnabled: boolean;

  /** Whether tooltip is enabled in this level or not */
  tooltip: boolean;

  /** Determines the bounds for where this element can be dragged. */
  dragId: string;

  positionMonitor: FormulaPositionMonitor;

  /** The offset from the starting position of the element. */
  position: PositionedNode | undefined;

  split: 'above' | 'below' | undefined;
}

interface State {
  isDragging: boolean;
  tooltip: boolean;
}

/**
 * Component for displaying a `Formula`.
 */
class FormulaBox extends Component<Props, State> {
  static contextType = ScaleContext;

  private readonly reference: RefObject<HTMLDivElement>;

  constructor(props: Props) {
    super(props);
    this.state = { isDragging: false, tooltip: this.props.tooltip };
    this.reference = React.createRef();
  }

  componentDidMount() {
    const node = this.reference.current;

    if (node === null) {
      throw Error('DOM elements missing');
    }

    this.props.positionMonitor.onCreate(this.props.formula.id, node);
  }

  render() {
    const offset: Point =
      this.props.position !== undefined
        ? this.props.position.offset
        : Point.origin;

    const center: Point =
      this.props.position !== undefined
        ? this.props.position.center
        : Point.origin;

    const splitStyle =
      this.props.split === 'above'
        ? styles.splitAbove
        : this.props.split === 'below'
        ? styles.splitBelow
        : styles.noSplit;

    return (
      <Draggable
        disabled={!this.props.draggingEnabled}
        bounds={'#' + this.props.dragId}
        handle={'.' + styles.handle}
        onStart={this.dragStart}
        onStop={this.dragStop}
        defaultClassNameDragging={styles.dragging}
        position={offset}
        scale={this.context}
      >
        <div className={styles.dragger} ref={this.reference}>
          <div className={classNames(styles.formulaBox, splitStyle)}>
            <div className={classNames(styles.filter)} />
            <div
              className={classNames(styles.cover, {
                [styles.disabled]:
                  !this.props.draggingEnabled || !this.state.isDragging
              })}
            />
            <Tooltip
              open={this.state.tooltip}
              title="Drag Me!"
              placement="right-end"
              disableTouchListener={true}
            >
              <DragIcon
                className={classNames(styles.handle, {
                  [styles.disabled]: !this.props.draggingEnabled
                })}
              />
            </Tooltip>
            {this.props.formula.render(this.props.subgoal, this.props.executor)}
            <span className={styles.debugPosition}>
              Center: {center.x}, {center.y} <br />
              Offset: {offset.x}, {offset.y} <br />
            </span>
          </div>
        </div>
      </Draggable>
    );
  }

  private dragStart = () => {
    this.setState({ isDragging: true, tooltip: false });
  };

  private dragStop = (e: DraggableEvent, data: DraggableData) => {
    this.setState({ isDragging: false });
    this.onDrag(e, data);
  };

  private onDrag = (_e: DraggableEvent, data: DraggableData) => {
    this.props.positionMonitor.onDrag(this.props.formula.id, data);
  };
}

export default FormulaBox;
